'use client'

import { useState, useEffect } from 'react'
import {
	AppBar,
	Toolbar,
	IconButton,
	useTheme,
	useMediaQuery,
	Typography,
	Box,
	Grid,
	Button,
	SvgIcon,
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import { navItems } from '@/app/components/ui/navigation/nav'
import { useRouter, usePathname } from 'next/navigation'
import { useNavigation } from '@/lib/context/navigationcontext'
import { Sizes } from '@/lib/constants/sizes'
import { TypographyVariants } from '@/app/styles/themes/typography'
import ElevationOnScroll from '@/app/utilities/elevationonscroll'
import { Strings } from '@/lib/constants/strings'
import RecruitingTabGroup from '@agent360/recruiting/components/recruitingtabgroup'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { AIAssistnatIcon } from '@/public/icons/bi_icons'
import { pushToDataLayer } from '@/app/lib/analytics/analyticsTracking'
import { analyticsTags } from '@/app/lib/analytics/analyticsEventTags'
import { showAIAssistantsNavItem } from '@/app/utilities/navbarUtils'
import AIAssistantDrawer from '../aiAssistant/aiAssistantDrawer'
import { useCompanyContext } from '@/lib/context/companyContext'
import { PERMISSIONS } from '@/lib/constants/permissions'

export default function Navbar() {
	const isNewLayout = true
	const router = useRouter()
	const currentPath = usePathname()
	const { hasAcceptedLatestTerms, permissions } = useCompanyContext()

	const hasPermission = (permission: string) =>
		permissions?.includes(permission)

	const [isTnCPage, setIsTnCPage] = useState(false)
	const [isAIAssistantOpen, setIsAIAssistantOpen] = useState(false)
	const {
		selectedAppBarNavItem,
		isLeftNavExpanded,
		toggleLeftNavExpanded,
		showTopNav,
		showLeftNav,
		initialLogin,
		toggleInitialLogin,
		setIsLeftNavExpanded,
		selectedNavItem,
		setSelectedAppBarNavItem,
		setSelectedNavItem,
	} = useNavigation()
	const theme = useTheme()
	const isMobileScreen = useMediaQuery(theme.breakpoints.only('xs'))

	let leftPaddingValue: string | number = 0

	if (showLeftNav) {
		leftPaddingValue = isLeftNavExpanded
			? `${Sizes.LEFT_DRAWER_WIDTH}px`
			: `${Sizes.LEFT_DRAWER_COLLAPSE_WIDTH}px`
	}

	const recruitingLeftPadding = {
		xs: 0,
		md: leftPaddingValue,
	}

	const {
		tempBon1047StickyTabNavgationEnabled,
		tempBoss2058EnableAiAssistant,
		tempBoss2823NewDesign
	} = useFlags()

	const showRecruitingTabs = currentPath.startsWith('/feature/recruiting')

	const hideStickyTabMobile =
		currentPath.includes('/agentprofile') &&
		!tempBon1047StickyTabNavgationEnabled &&
		isMobileScreen

	useEffect(() => {
		setIsTnCPage(false)
		if (currentPath === '/') {
			setSelectedAppBarNavItem(navItems[0])
			setSelectedNavItem(navItems[0])
		} else if (currentPath === '/feature/termsandconditions') {
			setIsTnCPage(true)
		}
	}, [
		// isSessionExpired,
		router,
		currentPath,
		initialLogin,
		isLeftNavExpanded,
		isMobileScreen,
		selectedAppBarNavItem,
		selectedNavItem,
		setSelectedAppBarNavItem,
		setSelectedNavItem,
	])

	function handleMenuClick() {
		if (!isMobileScreen) {
			toggleLeftNavExpanded?.(isLeftNavExpanded || false)
		} else {
			setIsLeftNavExpanded?.(true)
		}
		toggleInitialLogin?.(true)
	}

	function handleAIAssistantToggle() {
		pushToDataLayer(analyticsTags.biAIAssistedChatButton)
		setIsAIAssistantOpen(!isAIAssistantOpen)
	}

	return (
		<ElevationOnScroll isNewLayout={isNewLayout}>
			<AppBar
				position="fixed"
				sx={{
					justifyContent: 'center',
					...(isNewLayout && {
						borderBottom: `1px solid ${theme.palette.stroke.neutral.low}`,
					}),
				}}
				data-testid="top-nav"
			>
				<Grid container alignItems="center" justifyContent="space-between">
					<Grid item>
						<Toolbar variant="dense">
							{isMobileScreen && showLeftNav && (
								<IconButton
									size="large"
									edge="start"
									color="inherit"
									aria-label="Mobile button"
									id="mobile-button"
									aria-haspopup="true"
									sx={{ px: '20px' }}
									onClick={handleMenuClick}
								>
									<MenuIcon data-testid="menuIcon" />
								</IconButton>
							)}

							<Typography
								variant={TypographyVariants.mediumSemiBold}
								sx={{
									color: theme.palette.foreground.neutral.onSurface,
									display: { xs: 'inline-block' },
									pl:
										isTnCPage && !showLeftNav
											? { xs: 2, sm: 5 }
											: {
												xs: showLeftNav ? 0 : '7%',
												sm: isLeftNavExpanded
													? `${Sizes.LEFT_DRAWER_WIDTH + Sizes.NAV_LEFT_PADDING}px`
													: `${Sizes.LEFT_DRAWER_COLLAPSE_WIDTH + Sizes.NAV_LEFT_PADDING}px`,
											},
								}}
								data-testid="navbar-title"
							>
								{(() => {
									if (isTnCPage) {
										return Strings.TNC_NAV_TITLE
									}
									if (showTopNav && selectedAppBarNavItem === undefined) {
										return undefined
									}
									return selectedAppBarNavItem?.label
								})()}
							</Typography>
						</Toolbar>
					</Grid>
					{hasPermission(PERMISSIONS.AI_ASSISTANCE_ACCESS) && tempBoss2058EnableAiAssistant &&
						showAIAssistantsNavItem(currentPath, !!hasAcceptedLatestTerms) && (
							<Grid item xs="auto">
								{isMobileScreen ? (
									<Button
										onClick={handleAIAssistantToggle}
										variant="bespoke"
										color="secondary"
										size="small"
										sx={{
											background: theme.palette.background.default,
											borderRadius: '20px',
											border: 'none',
											minWidth: 'unset',
											width: '40px',
											height: '40px',
											marginRight: '15px',
										}}
									>
										<SvgIcon
											component={AIAssistnatIcon}
											fontSize="small"
											sx={{
												height: '20px',
												width: '20px',
												marginBottom: '-5px',
												marginRight: '-7px',
											}}
										/>
									</Button>
								) : (
									<Box sx={{ mr: '45px' }}>
										<Box
											sx={{
												position: 'relative',
												background: `linear-gradient(to bottom, ${theme.palette.surface.brand.aiGradientPurple}, ${theme.palette.surface.brand.aiGradientBlue})`,
												borderRadius: '4px',
												padding: '1.4px',
											}}
										>
											<Button
												onClick={handleAIAssistantToggle}
												variant="bespoke"
												color="secondary"
												size="small"
												sx={{
													'background': theme.palette.background.paper,
													'borderRadius': '2px',
													'border': 'none',
													'width': '100%',
													'&:hover': {
														background: theme.palette.background.paper,
													},
												}}
											>
												<SvgIcon
													component={AIAssistnatIcon}
													fontSize="small"
													sx={{
														height: { xs: '16px', sm: '20px' },
														width: { xs: '16px', sm: '20px' },
														marginBottom: '-5px',
													}}
												/>
												{Strings.AI_ASSISTANT_LABEL}
											</Button>
										</Box>
									</Box>
								)}
								<AIAssistantDrawer
									isOpen={isAIAssistantOpen}
									onClose={() => setIsAIAssistantOpen(false)}
									title={Strings.AI_ASSISTANT_LABEL}
								/>
							</Grid>
						)}
				</Grid>
				{!tempBoss2823NewDesign && showRecruitingTabs && !hideStickyTabMobile && (
					<>
						<Box sx={{ height: '10px' }} aria-hidden />
						<Box
							sx={{
								backgroundColor: theme.palette.background.paper,
							}}
						>
							<Box
								sx={{
									pl: recruitingLeftPadding,
									height: 32,
									display: 'flex',
									alignItems: 'center',
								}}
							>
								<Box
									sx={{
										width: '100%',
										display: 'flex',
										justifyContent: 'center',
									}}
								>
									<Box sx={{ flex: 1, maxWidth: '100%' }}>
										<RecruitingTabGroup />
									</Box>
								</Box>
							</Box>
						</Box>
					</>
				)}
			</AppBar>
		</ElevationOnScroll>
	)
}
